import { useEffect, useMemo, useState } from 'react';
import type { PcMonitorListingsResponse, PcMonitorListing } from '../../types/pcmonitor.ts';
import { sortByValueGap } from '../../components/pcmonitor/listingMath.ts';
import CandidateFilterBar, { type PcMonitorSort } from '../../components/pcmonitor/CandidateFilterBar.tsx';
import ListingRow from '../../components/pcmonitor/ListingRow.tsx';
import ListingCard from '../../components/pcmonitor/ListingCard.tsx';
import StalenessLine from '../../components/pcmonitor/StalenessLine.tsx';
import DatasetError from '../../components/pcmonitor/DatasetError.tsx';
import EmptyCandidates from '../../components/pcmonitor/EmptyCandidates.tsx';
import { Spinner } from '../../components/Spinner.tsx';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message?: string; fetchedAt?: string }
  | { kind: 'ready'; data: PcMonitorListingsResponse };

function isRejected(listing: PcMonitorListing): boolean {
  return listing.verdict === 'rejected';
}

function sortListings(listings: PcMonitorListing[], sort: PcMonitorSort): PcMonitorListing[] {
  if (sort === 'gap') return sortByValueGap(listings);
  if (sort === 'newest') {
    return [...listings].sort((a, b) => {
      const da = a.posted_date ? new Date(a.posted_date).getTime() : null;
      const db = b.posted_date ? new Date(b.posted_date).getTime() : null;
      if (da != null && db != null) return db - da;
      if (da != null) return -1;
      if (db != null) return 1;
      return 0;
    });
  }
  // price_asc — unresolved prices sink to the bottom, never a number shown.
  return [...listings].sort((a, b) => {
    if (a.asking_price != null && b.asking_price != null) return a.asking_price - b.asking_price;
    if (a.asking_price != null) return -1;
    if (b.asking_price != null) return 1;
    return 0;
  });
}

export default function PcMonitorPage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [includeRejected, setIncludeRejected] = useState(false);
  const [sort, setSort] = useState<PcMonitorSort>('gap');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/pc-monitor/listings')
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          let message: string | undefined;
          try {
            const body = await res.json();
            message = body?.message || body?.error;
          } catch {
            message = `HTTP ${res.status}`;
          }
          setState({ kind: 'error', message, fetchedAt: new Date().toISOString() });
          return;
        }
        const data = (await res.json()) as PcMonitorListingsResponse;
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
            fetchedAt: new Date().toISOString(),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const listings = state.data.listings ?? [];
    const filtered = includeRejected ? listings : listings.filter((l) => !isRejected(l));
    return sortListings(filtered, sort);
  }, [state, includeRejected, sort]);

  const counts = useMemo(() => {
    const listings = state.kind === 'ready' ? (state.data.listings ?? []) : [];
    return {
      candidates: listings.filter((l) => !isRejected(l)).length,
      rejected: listings.filter(isRejected).length,
    };
  }, [state]);

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">PC Monitor</h1>
        <p className="text-sm text-gray-500">
          Candidate machines from tutti.ch and ricardo.ch, ranked by how far the asking price sits
          below estimated parts value.
        </p>
        {state.kind === 'ready' && <StalenessLine generatedAt={state.data.generated_at} />}
      </header>

      {state.kind === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
          <Spinner /> Loading candidates…
        </div>
      )}

      {state.kind === 'error' && (
        <DatasetError message={state.message} fetchedAt={state.fetchedAt} />
      )}

      {state.kind === 'ready' && (
        <>
          <CandidateFilterBar
            includeRejected={includeRejected}
            onIncludeRejectedChange={setIncludeRejected}
            counts={counts}
            sort={sort}
            onSortChange={setSort}
          />
          {visible.length === 0 ? (
            <EmptyCandidates includeRejected={includeRejected} />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block border border-gray-200 rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                      <th className="py-2 pr-3 pl-3 font-medium">Photo</th>
                      <th className="py-2 pr-3 font-medium">Listing</th>
                      <th className="py-2 pr-3 font-medium">Age</th>
                      <th className="py-2 pr-3 font-medium">Asking</th>
                      <th className="py-2 pr-3 font-medium">Est. value</th>
                      <th className="py-2 pr-3 font-medium">Gap</th>
                      <th className="py-2 pr-3 font-medium">Drop</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((listing) => (
                      <ListingRow key={listing.id} listing={listing} />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile stacked cards */}
              <div className="md:hidden space-y-3">
                {visible.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
